import type { BimWorkOrderVersionTotalItemData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef, RowClassRules, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';

import { bimNumberFormatter } from '../../BimWorkOrderPriceVersionTable/cellRenderers';

const quantityValueFormatter = (p: ValueFormatterParams) => {
    const { value, data } = p;

    if (!value) {
        return '';
    }

    if (data?.name?.includes('в т.ч')) {
        return `${formatNumberToRu(value, 2)}%`;
    }
    if (data?.name?.includes('инфляции на')) {
        return `${formatNumberToRu(value, 3)}`;
    }

    return formatNumberToRu(value, 0);
};

export const useBimWorkOrderTotalVersionColumns = ({
    workOrderStatus,
    hasInProgress,
    isFrameContract,
}: {
    workOrderStatus: WorkOrderStatusEnum;
    hasInProgress: boolean;
    isFrameContract: boolean;
}) => {
    const planColumns = useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                headerName: 'Стоимость строительно-монтажных работ',
                width: 600,
                children: [
                    {
                        headerName: 'Всего СМР',
                        field: 'planValue.smrTotal',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                    },
                    {
                        headerName: 'ОЗП',
                        field: 'planValue.ozp',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                    },
                    {
                        headerName: 'ЭММ',
                        field: 'planValue.emm',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                    },
                    {
                        headerName: 'В том числе',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        children: [
                            {
                                headerName: 'ЗПМ',
                                field: 'planValue.zpm',
                                minWidth: 180,
                                spanHeaderHeight: true,
                                valueFormatter: bimNumberFormatter(0),
                            },
                        ],
                        marryChildren: true,
                    },
                    {
                        headerName: 'МП',
                        field: 'planValue.mp',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                    },
                    {
                        headerName: 'НР',
                        field: 'planValue.nr',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                    },
                    {
                        headerName: 'СП',
                        field: 'planValue.sp',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                    },
                ],
                marryChildren: true,
            },
            {
                headerName: 'ОП',
                field: 'planValue.op',
                minWidth: 120,
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
            },
            {
                headerName: 'Прочие затраты',
                field: 'planValue.others',
                minWidth: 120,
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
            },
            {
                headerName: 'Всего',
                field: 'planValue.total',
                minWidth: 120,
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
            },
            {
                headerName: 'Справочно МЗ',
                field: 'planValue.mz',
                minWidth: 140,
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
            },
            {
                headerName: 'Справочно ОЗ',
                field: 'planValue.oz',
                minWidth: 140,
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
            },
            {
                headerName: 'ТЗР, чел.час',
                field: 'planValue.tzr',
                minWidth: 140,
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(2),
            },
        ],
        [],
    );

    const isUseInProgressColumnsSetup = useMemo(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress ||
            workOrderStatus === WorkOrderStatusEnum.Completed ||
            (workOrderStatus === WorkOrderStatusEnum.Delayed && hasInProgress),
        [hasInProgress, workOrderStatus],
    );

    const columns = useMemo<ColDef<BimWorkOrderVersionTotalItemData>[]>(
        () => [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                minWidth: 230,
                wrapText: true,
                autoHeight: true,
                cellClass: 'wrappedCenteredCell',
                suppressMovable: true,
            },
            {
                headerName: 'Кол-во',
                field: 'planValue.quantity',
                minWidth: 100,
                valueFormatter: quantityValueFormatter,
            },
            ...(!isUseInProgressColumnsSetup
                ? planColumns
                : [
                      {
                          headerName: `План по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          children: planColumns,
                          marryChildren: true,
                      },
                  ]),
            {
                headerName: `Выполнено по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                field: 'completedValue.total',
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
                hide: !isUseInProgressColumnsSetup,
                minWidth: 160,
            },
            {
                headerName: `Остаток по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                field: 'remainderValue.total',
                spanHeaderHeight: true,
                valueFormatter: bimNumberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
                hide: !isUseInProgressColumnsSetup,
                minWidth: 200,
                flex: 1,
            },
        ],
        [isFrameContract, isUseInProgressColumnsSetup, planColumns],
    );

    const bimTotalsTableRowClassRules: RowClassRules = useMemo(
        () => ({
            totalsRow: ({ data }: { data: BimWorkOrderVersionTotalItemData }) =>
                data?.name?.toLowerCase().includes('итого по главам') ||
                data?.name?.toLowerCase().includes('итого по наряд-заказу') ||
                data?.name?.toLowerCase().includes('итого по расчету') ||
                data?.name?.toLowerCase().includes('итого по виду строительства') ||
                data?.name?.toLowerCase().includes('итого с непредвиденными и мотивационными затратами'),
        }),
        [],
    );

    return {
        columns,
        bimTotalsTableRowClassRules,
    };
};
